import * as React from 'react'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import Tooltip from '@mui/material/Tooltip'

import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'

import { AppStorage } from 'src/utils'
import { classToFComponent, FCClassContext, IconActionButton, ListagemTab, ListagemTabs } from 'src/utils/views'
import { Logger } from 'wdc-cube'

import ListagemEmpresaView from '../../the-consumer-market/views/tcm_listagem_empresa'
import QsaListagemView from '../../the-consumer-market/views/tcm_listagem_qsa'
import { ListagemTabsScope, type TcListagemTabsOption } from '../tc_scopes'

const LOG = Logger.get('TC-VIEW-LIST-TABS')
const appStorage = AppStorage.singleton()

const TabStrip = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 10,
    flexShrink: 0
})

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        minHeight: 0
    },
    tab_content: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        minHeight: 0
    }
})

export type ListagemTabsViewProps = {
    className?: string
    style?: React.CSSProperties
    scope: ListagemTabsScope
}

export class ListagemTabsViewClass implements FCClassContext<ListagemTabsViewProps> {
    scope!: ListagemTabsScope
    classes!: ReturnType<typeof useStyles>['classes']
    cx!: ReturnType<typeof useStyles>['cx']

    readonly emitExpand = () => this.scope.onExpandClick().catch(LOG.caught)
    readonly emitShrunk = () => this.scope.onShrunkClick().catch(LOG.caught)

    onSyncState(props: ListagemTabsViewProps) {
        this.scope = props.scope
        const { classes, cx } = useStyles()
        this.classes = classes
        this.cx = cx
    }

    render({ className, style }: ListagemTabsViewProps): React.JSX.Element {
        const { scope, classes, cx } = this

        const tabKeys: TcListagemTabsOption[] = []
        appStorage.tcCompaniesEnabled && tabKeys.push('pj')
        appStorage.tcPartnersEnabled && tabKeys.push('qsa')

        if (tabKeys.length === 0) {
            return <></>
        }

        let tabIdx = tabKeys.indexOf(scope.active)
        if (tabIdx < 0) {
            tabIdx = 0
        }
        const activeTab = tabKeys[tabIdx]

        return (
            <div className={cx(classes.view, className)} style={style}>
                <TabStrip>
                    <ListagemTabs value={tabIdx} onChange={this.handleTabChange} aria-label="Listagens" role="tablist">
                        {tabKeys.map((key, idx) => (
                            <ListagemTab key={key} label={TAB_LABELS[key]} {...a11yProps(idx)} />
                        ))}
                    </ListagemTabs>
                    <Tooltip title={scope.expanded ? 'Restaurar listagens' : 'Maximizar listagens'}>
                        <IconActionButton
                            size="small"
                            aria-label={scope.expanded ? 'Restaurar listagens' : 'Maximizar listagens'}
                            onClick={scope.expanded ? this.emitShrunk : this.emitExpand}
                        >
                            {scope.expanded && <CloseFullscreenRoundedIcon fontSize="small" />}
                            {!scope.expanded && <LaunchRoundedIcon fontSize="small" />}
                        </IconActionButton>
                    </Tooltip>
                </TabStrip>
                <div className={classes.tab_content}>
                    {activeTab === 'pj' && <ListagemEmpresaView scope={scope.empresasListagem} />}
                    {activeTab === 'qsa' && <QsaListagemView scope={scope.qsaListagem} />}
                </div>
            </div>
        )
    }

    readonly handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const tabKeys: TcListagemTabsOption[] = []
        appStorage.tcCompaniesEnabled && tabKeys.push('pj')
        appStorage.tcPartnersEnabled && tabKeys.push('qsa')

        const key = tabKeys[newValue] ?? 'pj'
        this.scope.onSelect(key).catch(LOG.caught)
    }
}

const TAB_LABELS: Record<TcListagemTabsOption, string> = {
    pj: 'Empresas',
    qsa: 'Sócios'
}

export default classToFComponent(ListagemTabsViewClass, React)

function a11yProps(index: number) {
    return {
        id: `tc-listagem-tab-${index}`,
        'aria-controls': `tc-listagem-tabpanel-${index}`
    }
}
